import { supabase } from '../lib/supabase';

/**
 * AI API Configuration Service
 * Manages customer-owned API keys for multiple AI providers
 */

class AIApiConfigService {
  /**
   * Get AI API configuration from company settings metadata
   */
  async getConfig() {
    try {
      const { data, error } = await supabase?.from('company_settings')?.select('metadata')?.limit(1)?.maybeSingle();

      if (error) throw error;

      // Return AI config from metadata or empty object
      return data?.metadata?.ai_api_keys || {};
    } catch (error) {
      console.error('Error fetching AI API config:', error);
      throw new Error('Falha ao carregar configurações de IA');
    }
  }

  /**
   * Update AI API configuration in company settings metadata
   */
  async updateConfig(aiConfig) {
    try {
      // Get current settings
      const { data: currentSettings, error: fetchError } = await supabase?.from('company_settings')?.select('*')?.limit(1)?.maybeSingle();

      if (fetchError) throw fetchError;

      // If no settings exist, create default first
      if (!currentSettings) {
        const { data: newSettings, error: createError } = await supabase?.from('company_settings')?.insert({
            company_name: 'Minha Empresa',
            cnpj: '',
            estado_origem: 'SP',
            metadata: {
              ai_api_keys: aiConfig
            }
          })?.select()?.single();

        if (createError) throw createError;
        return newSettings?.metadata?.ai_api_keys || {};
      }

      // Update existing settings with merged metadata
      const updatedMetadata = {
        ...(currentSettings?.metadata || {}),
        ai_api_keys: aiConfig
      };

      const { data, error } = await supabase?.from('company_settings')?.update({ metadata: updatedMetadata })?.eq('id', currentSettings?.id)?.select()?.single();

      if (error) throw error;
      return data?.metadata?.ai_api_keys || {};
    } catch (error) {
      console.error('Error updating AI API config:', error);
      throw new Error('Falha ao atualizar configurações de IA');
    }
  }

  /**
   * Test connection to AI provider
   */
  async testConnection(provider, apiKey) {
    try {
      const startTime = Date.now();

      switch (provider) {
        case 'openai':
          const openaiResponse = await fetch('https://api.openai.com/v1/models', {
            headers: {
              'Authorization': `Bearer ${apiKey}`
            }
          });
          const responseTime = Date.now() - startTime;
          
          if (!openaiResponse?.ok) {
            const errorData = await openaiResponse?.json();
            throw new Error(errorData?.error?.message || 'Falha na conexão');
          }
          
          return { success: true, responseTime, message: 'Conexão bem-sucedida' };

        case 'gemini':
          const geminiResponse = await fetch(
            `https://generativelanguage.googleapis.com/v1/models?key=${apiKey}`
          );
          const geminiTime = Date.now() - startTime;
          
          if (!geminiResponse?.ok) {
            const errorData = await geminiResponse?.json();
            throw new Error(errorData?.error?.message || 'Falha na conexão');
          }
          
          return { success: true, responseTime: geminiTime, message: 'Conexão bem-sucedida' };

        case 'anthropic':
          const anthropicResponse = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'x-api-key': apiKey,
              'anthropic-version': '2023-06-01',
              'content-type': 'application/json'
            },
            body: JSON.stringify({
              model: 'claude-3-haiku-20240307',
              max_tokens: 10,
              messages: [{ role: 'user', content: 'test' }]
            })
          });
          const anthropicTime = Date.now() - startTime;
          
          if (!anthropicResponse?.ok) {
            const errorData = await anthropicResponse?.json();
            throw new Error(errorData?.error?.message || 'Falha na conexão');
          }
          
          return { success: true, responseTime: anthropicTime, message: 'Conexão bem-sucedida' };

        default:
          throw new Error('Provedor não suportado');
      }
    } catch (error) {
      console.error(`Error testing ${provider} connection:`, error);
      return {
        success: false,
        responseTime: 0,
        message: error?.message || 'Erro ao testar conexão'
      };
    }
  }

  /**
   * Mask API key for display (show first 8 and last 4 characters)
   */
  maskApiKey(apiKey) {
    if (!apiKey || apiKey?.length < 12) return '••••••••••••';
    return `${apiKey?.substring(0, 8)}••••${apiKey?.substring(apiKey?.length - 4)}`;
  }
}

export const aiApiConfigService = new AIApiConfigService();